import { ChangeEvent, useEffect, useState } from 'react'

import { Button, Checkbox, Heading, Spinner, Text } from '@plurall/elo-beta'
import queryString from 'query-string'

import { ACCEPT_TERM_OF_USE } from '../../../graphql/mutations'
import { GET_TERM_OF_USE, GET_TERMS_OF_USE } from '../../../graphql/queries'
import {
  dispatchToMobile,
  graphqlClient,
  handleGraphQLErrorsWithToast,
  isInApp,
  redirectToHome,
  RETURN_ACCOUNT,
} from '../../../utils'
import callToast from '../../../utils/callToast'
import styles from './TermsOfUse.module.scss'

const TermsOfUse = () => {
  const { hostname, protocol } = window.location
  const { redirect: redirectUrl } = queryString.parse(window.location.search)
  const url = `${protocol}//${hostname}`
  const [applications, setApplications] = useState<
    Array<{
      termsOfUse: { id: string; accepted: boolean }
    }>
  >([])
  const [isLoading, setIsLoading] = useState(true)
  const [termContent, setTermContent] = useState('')
  const [termTitle, setTermTitle] = useState('')
  const [termPublicationDate, setTermPublicationDate] = useState(null)
  const [isChecked, setChecked] = useState(false)
  const [isSubmittingTerm, setIsSubmittingTerm] = useState(false)

  const handleReturn = () => {
    if (redirectUrl && typeof redirectUrl === 'string') {
      window.location.href = redirectUrl
      return
    }

    redirectToHome()
  }

  const handleSubmit = async () => {
    if (!isChecked) {
      callToast({
        variant: 'attention',
        text: 'Você precisa aceitar os termos de uso para continuar',
      })
      return
    }

    try {
      setIsSubmittingTerm(true)

      await graphqlClient().mutate({
        mutation: ACCEPT_TERM_OF_USE,
        variables: { id: applications[0]?.termsOfUse.id },
      })

      if (isInApp()) {
        dispatchToMobile(RETURN_ACCOUNT)
        return
      }

      if (redirectUrl && typeof redirectUrl === 'string') {
        window.location.href = redirectUrl
        return
      }

      redirectToHome()

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      handleGraphQLErrorsWithToast(e, 'Não foi possível enviar o aceite dos termos de uso')
    } finally {
      setIsSubmittingTerm(false)
    }
  }

  useEffect(() => {
    const getTermsOfUse = async () => {
      try {
        const {
          data: {
            person: { applications },
          },
        } = await graphqlClient().query({
          query: GET_TERMS_OF_USE,
          variables: {
            url: redirectUrl || url,
          },
        })

        if (!applications.length) {
          handleReturn()
          return
        }

        const {
          data: {
            termOfUse: { content, title, publicationDate },
          },
        } = await graphqlClient().query({
          query: GET_TERM_OF_USE,
          variables: { id: applications[0]?.termsOfUse.id },
        })

        setTermContent(content)
        setTermTitle(title)
        setTermPublicationDate(publicationDate)
        setApplications(applications)
      } catch (e: unknown) {
        handleGraphQLErrorsWithToast(
          e,
          'Não foi possível carregar os termos de uso. Tente novamente mais tarde',
        )

        redirectToHome()
      } finally {
        setIsLoading(false)
      }
    }

    getTermsOfUse()
  }, [])

  if (isLoading) {
    return <Spinner />
  }

  return (
    <div>
      {applications[0]?.termsOfUse?.accepted ? (
        <>
          <div className={styles.title}>
            <Heading>Termos de Uso e Política de Privacidade</Heading>
          </div>

          {termPublicationDate && (
            <div className={styles['term-date']}>
              <Text bold>Atualizado em: {termPublicationDate}</Text>
            </div>
          )}
          <div
            className={styles['term-content']}
            dangerouslySetInnerHTML={{ __html: termContent }}
          />
          <div className={`${styles.button} ${styles['back-button']}`}>
            <Button onClick={handleReturn}>Voltar</Button>
          </div>
        </>
      ) : (
        <>
          <div className={styles.title}>
            <Heading>Atualização dos Termos de Uso e Política de Privacidade</Heading>
          </div>

          {termTitle && (
            <div className={styles['sub-title']} dangerouslySetInnerHTML={{ __html: termTitle }} />
          )}
          <div
            className={styles['term-content']}
            dangerouslySetInnerHTML={{ __html: termContent }}
          />

          <div className={styles.agreement}>
            <Text>
              {' '}
              Ao clicar em “ACEITAR” você concorda com os Termos de Uso e Política de Privacidade do
              Plurall.{' '}
            </Text>
          </div>
          <div className={styles.row}>
            <Checkbox
              name='accepted'
              value={isChecked.toString()}
              checked={isChecked}
              label='Confirmo que li os termos de uso'
              onChange={(e: ChangeEvent<HTMLInputElement>) => setChecked(e.target.checked)}
            />

            <Button disabled={!isChecked || isSubmittingTerm} onClick={() => handleSubmit()}>
              Aceitar
            </Button>
          </div>
        </>
      )}
    </div>
  )
}

export default TermsOfUse
